import moment from 'moment';
import { openPrintWindow, escapeHtml } from '../MedicineOrders/printDispenseAuthorization';

function fmtDate(value) {
    if (!value) return '—';
    try {
        return moment(value).format('YYYY/MM/DD HH:mm');
    } catch {
        return '—';
    }
}

export function buildStockVoucherHtml(detail, options) {
    const opts = options || {};
    const printTitle = escapeHtml(detail.voucherTypeLabel || 'سند مخزني');
    const docRef = escapeHtml(detail.code || String(detail.id || ''));
    const logoUrl = opts.logoUrl || '';
    const logoBlock = logoUrl
        ? `<img src="${escapeHtml(logoUrl)}" alt="" class="moh-logo" width="120" height="auto" />`
        : '';
    const rows = (detail.lines || []).map((line, index) => `
        <tr class="${index % 2 === 0 ? 'bg-gray-striped-1' : 'bg-gray-striped-2'}">
            <td>${index + 1}</td>
            <td>${escapeHtml(line.productName || '')}</td>
            <td>${Number(line.quantity || 0)}</td>
            <td>${escapeHtml(line.batchNumber || '—')}</td>
            <td>${line.expiryDate ? escapeHtml(moment(line.expiryDate).format('YYYY/MM/DD')) : '—'}</td>
            <td>${escapeHtml(line.note || '—')}</td>
        </tr>`).join('');

    const approvals = (detail.approvals || []).map((step, index) => `
        <tr class="${index % 2 === 0 ? 'bg-gray-striped-1' : 'bg-gray-striped-2'}">
            <td>${step.stepNo}</td>
            <td>${escapeHtml(step.requiredRoleLabel || step.requiredRole || '—')}</td>
            <td>${escapeHtml(step.decisionLabel || '—')}</td>
            <td>${escapeHtml(step.decidedByName || '—')}</td>
            <td>${escapeHtml(fmtDate(step.decidedOn))}</td>
            <td>${escapeHtml(step.note || '—')}</td>
        </tr>`).join('');

    return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${printTitle} - ${docRef}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap" rel="stylesheet">
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.0.2/dist/css/bootstrap.min.css" rel="stylesheet" crossorigin="anonymous" />
  <style>
    body { width:100%; margin:0; padding:0; background:#fafafa; color:#1a1a1a; font:11px "Cairo",sans-serif; -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
    * { box-sizing:border-box; }
    .moh-header { border-bottom:3px solid #0d6b4d; padding-bottom:8px; margin-bottom:12px; }
    .moh-brand { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
    .moh-logo { height:52px; width:auto; max-width:140px; object-fit:contain; flex-shrink:0; }
    .page-inner { padding:10mm; min-height:297mm; }
    .page-container { width:210mm; min-height:297mm; margin:10mm auto; border:1px solid #d3d3d3; border-radius:4px; background:#fff; box-shadow:0 0 5px rgba(0,0,0,.08); }
    .moh-title { color:#0d6b4d; font-weight:700; font-size:18px; letter-spacing:0.02em; }
    .moh-sub { font-size:12px; color:#444; margin-top:4px; }
    .doc-title { font-size:15px; font-weight:700; margin:8px 0 4px; }
    th, td { line-height:1.35; font-size:10px; vertical-align:middle !important; }
    .bg-gray { background-color:#b8c9c4 !important; color:#111; }
    .bg-gray-striped-1 { background-color:#eef4f2 !important; }
    .bg-gray-striped-2 { background-color:#f5f9f7 !important; }
    .print-footer { margin-top:16px; padding-top:10px; border-top:1px solid #ccc; font-size:9px; color:#555; line-height:1.6; }
    @page { size:A4; margin:0; }
    @media print {
      html, body { width:210mm; height:297mm; background:#fff; }
      .page-container { margin:0; border:none; box-shadow:none; width:100%; min-height:auto; }
    }
  </style>
</head>
<body>
  <div class="page-container">
    <div class="page-inner">
      <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div class="moh-header flex-grow-1">
          <div class="moh-brand">
            ${logoBlock}
            <div>
              <div class="moh-title">وزارة الصحة</div>
              <div class="moh-sub">منظومة إدارة المخازن والدواء</div>
            </div>
          </div>
        </div>
        <div class="text-end" style="min-width:140px;">
          <div class="doc-title">${printTitle}</div>
          <div class="small text-muted">تاريخ الطباعة: ${escapeHtml(moment().format('YYYY/MM/DD HH:mm'))}</div>
        </div>
      </div>

      <div class="row gx-3 mb-2">
        <div class="col-6">
          <table class="table table-sm table-bordered mb-2">
            <tbody>
              <tr class="bg-gray-striped-1"><th width="32%">رقم السند</th><td>${escapeHtml(detail.code || '—')}</td></tr>
              <tr class="bg-gray-striped-2"><th>النوع</th><td>${escapeHtml(detail.voucherTypeLabel || '—')}</td></tr>
              <tr class="bg-gray-striped-1"><th>الحالة</th><td>${escapeHtml(detail.statusLabel || '—')}</td></tr>
              <tr class="bg-gray-striped-2"><th>المرجع</th><td>${escapeHtml(detail.referenceCode || detail.sourceEntityType || '—')}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="col-6">
          <table class="table table-sm table-bordered mb-2">
            <tbody>
              <tr class="bg-gray-striped-1"><th width="32%">المرفق</th><td>${escapeHtml(detail.branchName || '—')}</td></tr>
              <tr class="bg-gray-striped-2"><th>القسم</th><td>${escapeHtml(detail.departmentName || '—')}</td></tr>
              <tr class="bg-gray-striped-1"><th>المخزن</th><td>${escapeHtml(detail.stockingName || '—')}</td></tr>
              <tr class="bg-gray-striped-2"><th>أُنشئ بواسطة</th><td>${escapeHtml(detail.createdByName || '—')}</td></tr>
              <tr class="bg-gray-striped-1"><th>تاريخ الإنشاء</th><td>${escapeHtml(fmtDate(detail.createdOn))}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="mb-3"><strong>ملاحظة:</strong> ${escapeHtml(detail.note || '—')}</div>

      <table class="table table-sm table-bordered text-center border border-dark align-middle">
        <thead>
          <tr class="bg-gray">
            <th>#</th>
            <th>الصنف</th>
            <th>الكمية</th>
            <th>التشغيلة</th>
            <th>الصلاحية</th>
            <th>ملاحظة</th>
          </tr>
        </thead>
        <tbody>${rows || '<tr><td colspan="6" class="text-center">لا توجد بنود</td></tr>'}</tbody>
      </table>

      <h6 class="mt-3">مسار الاعتماد</h6>
      <table class="table table-sm table-bordered text-center align-middle">
        <thead>
          <tr class="bg-gray">
            <th>الخطوة</th>
            <th>الدور</th>
            <th>القرار</th>
            <th>المنفذ</th>
            <th>التاريخ</th>
            <th>الملاحظة</th>
          </tr>
        </thead>
        <tbody>${approvals || '<tr><td colspan="6" class="text-center">لا توجد خطوات اعتماد مسجلة</td></tr>'}</tbody>
      </table>

      <div class="row text-center mt-4">
        <div class="col-4"><strong>إعداد السند</strong><br/>الاسم: ............................<br/>التوقيع: ............................</div>
        <div class="col-4"><strong>اعتماد السند</strong><br/>الاسم: ............................<br/>التوقيع والختم: ............................</div>
        <div class="col-4"><strong>الأرشفة/الاستلام</strong><br/>الاسم: ............................<br/>التوقيع: ............................</div>
      </div>

      <div class="print-footer">
        <p class="mb-1"><strong>بطاقة المستند:</strong> وثيقة مخزنية معتمدة للطباعة والأرشفة ضمن إجراءات ${escapeHtml(
        opts.buildFor || 'وزارة الصحة'
    )}. لا يُعتد بالنسخ الناقصة التوقيع أو الختم.</p>
        <p class="mb-0 small">مرجع المستند: <strong>${docRef}</strong>${opts.buildFor ? ` - ${escapeHtml(opts.buildFor)}` : ''}</p>
      </div>
    </div>
  </div>
</body>
</html>`;
}

export function printStockVoucher(detail, options) {
    return openPrintWindow(buildStockVoucherHtml(detail, options));
}
